/*
 * SeoProposalScorecard
 * Checks an SEO proposal the reader has received against the article's must-haves and red flags.
 *
 * Rules:
 * - MUST_HAVES: each one the reader does NOT tick is listed under "Missing from the proposal",
 *   with the exact question to ask the agency.
 * - RED_FLAGS: each one the reader DOES tick is listed under "Red flags", with why it matters.
 * - Results appear once the reader ticks anything OR presses "Check this proposal".
 * - Verdict:
 *     any red flag                         -> "Pause before signing"
 *     no red flags, 1+ must-haves missing  -> "Ask for these in writing before signing"
 *     no red flags, nothing missing        -> "This proposal covers the basics"
 * No scores, benchmarks, tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Item = { id: string; label: string; detail: string };

const MUST_HAVES: Item[] = [
  { id: "content", label: "How many pages or posts you get each month", detail: "How many practice-area pages and blog posts will you deliver each month?" },
  { id: "links", label: "Whether link building is included or billed separately", detail: "Is link building included in this fee? If not, what does it cost and how are links earned?" },
  { id: "tech", label: "Hours or tasks set aside for technical fixes", detail: "How many hours a month go to technical fixes, and how often do you re-audit the site?" },
  { id: "local", label: "Specific local SEO tasks (profile, listings, location pages)", detail: "What exactly will you do each month for our Google Business Profile, listings and location pages?" },
  { id: "exit", label: "What happens if you pause or cancel", detail: "What notice do we need to give to pause or cancel, and are there any exit costs?" },
  { id: "own", label: "Your firm owns the website, content and accounts", detail: "Confirm in writing that we own the website, content, Google Business Profile and analytics accounts." },
  { id: "report", label: "Monthly reporting on calls, forms and cases", detail: "Will the monthly report show calls, form submissions and signed cases, not just traffic and rankings?" },
];

const RED_FLAGS: Item[] = [
  { id: "guarantee", label: "Guarantees a specific ranking or position by a date", detail: "Google doesn't guarantee indexing or ranking for any site, so nobody can promise a position by a date." },
  { id: "secret", label: "Mentions a “proprietary method” or “secret formula”", detail: "Usually a template sold as a strategy. Ask them to explain exactly what they'll do for your market." },
  { id: "hosting", label: "Wants to host your website on their own platform", detail: "Leaving later can be slow and expensive. Keep the site on hosting your firm controls." },
  { id: "vague", label: "Describes the work only in general terms (e.g. “comprehensive SEO management”)", detail: "A promise with no specifics is easy to renegotiate in the agency's favor later." },
];

export default function SeoProposalScorecard() {
  const [has, setHas] = useState<Record<string, boolean>>({});
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [checked, setChecked] = useState(false);

  const touched = checked || Object.values(has).some(Boolean) || Object.values(flags).some(Boolean);
  const missing = MUST_HAVES.filter((m) => !has[m.id]);
  const redFlags = RED_FLAGS.filter((r) => flags[r.id]);

  let verdict = "";
  if (redFlags.length > 0) verdict = "Pause before signing. This proposal has red flags to resolve first.";
  else if (missing.length > 0) verdict = "Ask for these in writing before you sign.";
  else verdict = "This proposal covers the basics in writing.";

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const toggleBtn = (pressed: boolean) =>
    `h-auto w-full justify-start whitespace-normal rounded-lg border px-3 py-2 text-left text-sm ${focusRing} ${
      pressed ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
    }`;

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Check an SEO proposal before you sign</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Have a proposal in front of you? Tick what it actually says in writing. Nothing is saved or sent.
      </p>

      <fieldset className="mt-5">
        <legend className="text-sm font-semibold">The proposal states, in writing…</legend>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {MUST_HAVES.map((m) => (
            <li key={m.id}>
              <Button variant="outline"
                type="button"
                aria-pressed={!!has[m.id]}
                onClick={() => setHas((p) => ({ ...p, [m.id]: !p[m.id] }))}
                className={toggleBtn(!!has[m.id])}
              >
                <span aria-hidden="true">{has[m.id] ? "✓ " : "○ "}</span>
                {m.label}
              </Button>
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset className="mt-5">
        <legend className="text-sm font-semibold">The proposal or sales pitch…</legend>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {RED_FLAGS.map((r) => (
            <li key={r.id}>
              <Button variant="outline"
                type="button"
                aria-pressed={!!flags[r.id]}
                onClick={() => setFlags((p) => ({ ...p, [r.id]: !p[r.id] }))}
                className={toggleBtn(!!flags[r.id])}
              >
                <span aria-hidden="true">{flags[r.id] ? "! " : "○ "}</span>
                {r.label}
              </Button>
            </li>
          ))}
        </ul>
      </fieldset>

      <Button variant="outline"
        type="button"
        onClick={() => setChecked(true)}
        className={`mt-5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ${focusRing}`}
      >
        Check this proposal
      </Button>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {!touched ? (
          <p className="text-sm text-muted-foreground">Tick what applies, then check the proposal.</p>
        ) : (
          <div>
            <p className="text-sm font-semibold">{verdict}</p>

            {redFlags.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-semibold">Red flags</p>
                <ul className="mt-2 space-y-2">
                  {redFlags.map((r) => (
                    <li key={r.id} className="rounded-md border border-border bg-card p-3 text-sm">
                      <p className="font-medium">{r.label}</p>
                      <p className="mt-1 text-muted-foreground">{r.detail}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {missing.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-semibold">Missing from the proposal: questions to ask</p>
                <ul className="mt-2 space-y-2">
                  {missing.map((m) => (
                    <li key={m.id} className="rounded-md border border-border bg-card p-3 text-sm">
                      <p className="font-medium">{m.label}</p>
                      <p className="mt-1 text-muted-foreground">Ask: “{m.detail}”</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="mt-4 text-sm">
              Want a second opinion on what your firm actually needs?{" "}
              <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Get a free SEO audit →
              </Link>{" "}
              or{" "}
              <Link to="/pricing" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                see how we scope and price SEO →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
